import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import ClientOnly from "./components/ClientOnly";
import { ThemeProvider } from "./contexts/ThemeContext";
import { lazy, Suspense } from "react";
import { useEffect } from "react";
import { useLocation } from "wouter";

// Core pages - eagerly loaded for fast initial navigation
import Home from "./pages/Home";
import Tjanster from "./pages/Tjanster";
import Laserskarning from "./pages/Laserskarning";
import Kantpressning from "./pages/Kantpressning";
import Svetsning from "./pages/Svetsning";
import Maskinpark from "./pages/Maskinpark";
import OmOss from "./pages/OmOss";
import Referenser from "./pages/Referenser";
import CaseVolvo from "./pages/CaseVolvo";
import CaseForsvar from "./pages/CaseForsvar";
import KontaktSSR from "./pages/KontaktSSR";
import Policyer from "./pages/Policyer";
import Integritetspolicy from "./pages/Integritetspolicy";
import Villkor from "./pages/Villkor";
import RedaktionellPolicy from "./pages/RedaktionellPolicy";
import Tack from "./pages/Tack";
import LaserskarningStockholm from "./pages/LaserskarningStockholm";
import LaserskarningVasteras from "./pages/LaserskarningVasteras";
import LaserskarningOrebro from "./pages/LaserskarningOrebro";
import LaserskarningNorrkoping from "./pages/LaserskarningNorrkoping";
import LaserskarningLinkoping from "./pages/LaserskarningLinkoping";
import LaserskarningEskilstuna from "./pages/LaserskarningEskilstuna";
import LaserskarningSodertalje from "./pages/LaserskarningSodertalje";
import LaserskarningStrangnas from "./pages/LaserskarningStrangnas";
import LaserskarningKatrineholm from "./pages/LaserskarningKatrineholm";
import LaserskarningNykoping from "./pages/LaserskarningNykoping";
import Miljopolicy from "./pages/Miljopolicy";
import Arbetsmiljopolicy from "./pages/Arbetsmiljopolicy";
import Kvalitetspolicy from "./pages/Kvalitetspolicy";
import Nyheter from "./pages/Nyheter";
import AdminDashboard from "./pages/AdminDashboard";

// Blog articles - lazy loaded for code-splitting (53 articles)
const BlogLissmac = lazy(() => import("./pages/BlogLissmac"));
const BlogLaserVsPlasma = lazy(() => import("./pages/BlogLaserVsPlasma"));
const BlogMaterialguide = lazy(() => import("./pages/BlogMaterialguide"));
const BlogCadFiler = lazy(() => import("./pages/BlogCadFiler"));
const BlogTjocklekGuide = lazy(() => import("./pages/BlogTjocklekGuide"));
const BlogRostfritt = lazy(() => import("./pages/BlogRostfritt"));
const BlogAluminium = lazy(() => import("./pages/BlogAluminium"));
const BlogHardoxStrenx = lazy(() => import("./pages/BlogHardoxStrenx"));
const BlogPrecision = lazy(() => import("./pages/BlogPrecision"));
const BlogKostnader = lazy(() => import("./pages/BlogKostnader"));
const BlogLaserVsVattenjet = lazy(() => import("./pages/BlogLaserVsVattenjet"));
const BlogPrototyper = lazy(() => import("./pages/BlogPrototyper"));
const BlogStoraSerier = lazy(() => import("./pages/BlogStoraSerier"));
const BlogKantpressningGuide = lazy(() => import("./pages/BlogKantpressningGuide"));
const BlogKantpressToleranser = lazy(() => import("./pages/BlogKantpressToleranser"));
const BlogKantpressMaterial = lazy(() => import("./pages/BlogKantpressMaterial"));
const BlogKantpressVerktyg = lazy(() => import("./pages/BlogKantpressVerktyg"));
const BlogKantpressKonstruktion = lazy(() => import("./pages/BlogKantpressKonstruktion"));
const BlogKantpressVsRull = lazy(() => import("./pages/BlogKantpressVsRull"));
const BlogSvetsMetoder = lazy(() => import("./pages/BlogSvetsMetoder"));
const BlogSvetsRostfritt = lazy(() => import("./pages/BlogSvetsRostfritt"));
const BlogSvetsAluminium = lazy(() => import("./pages/BlogSvetsAluminium"));
const BlogSvetsKvalitet = lazy(() => import("./pages/BlogSvetsKvalitet"));
const BlogSvetsKonstruktion = lazy(() => import("./pages/BlogSvetsKonstruktion"));
const BlogStalsorter = lazy(() => import("./pages/BlogStalsorter"));
const BlogRostfritt304vs316 = lazy(() => import("./pages/BlogRostfritt304vs316"));
const BlogAluminiumLegeringar = lazy(() => import("./pages/BlogAluminiumLegeringar"));
const BlogYtbehandling = lazy(() => import("./pages/BlogYtbehandling"));
const BlogKorrosionsskydd = lazy(() => import("./pages/BlogKorrosionsskydd"));
const BlogMaterialval = lazy(() => import("./pages/BlogMaterialval"));
const BlogHardoxSlitplat = lazy(() => import("./pages/BlogHardoxSlitplat"));
const BlogRitningTillDetalj = lazy(() => import("./pages/BlogRitningTillDetalj"));
const BlogIsoCertifiering = lazy(() => import("./pages/BlogIsoCertifiering"));
const BlogAvgradning = lazy(() => import("./pages/BlogAvgradning"));
const BlogForstaBitKontroll = lazy(() => import("./pages/BlogForstaBitKontroll"));
const BlogNestning = lazy(() => import("./pages/BlogNestning"));
const BlogLedtider = lazy(() => import("./pages/BlogLedtider"));
const BlogFordon = lazy(() => import("./pages/BlogFordon"));
const BlogForsvar = lazy(() => import("./pages/BlogForsvar"));
const BlogBygg = lazy(() => import("./pages/BlogBygg"));
const BlogJordbruk = lazy(() => import("./pages/BlogJordbruk"));
const BlogEnergi = lazy(() => import("./pages/BlogEnergi"));
const BlogLivsmedel = lazy(() => import("./pages/BlogLivsmedel"));
const BlogMarin = lazy(() => import("./pages/BlogMarin"));
const BlogDesign = lazy(() => import("./pages/BlogDesign"));
const BlogAutomation = lazy(() => import("./pages/BlogAutomation"));
const BlogHallbarhet = lazy(() => import("./pages/BlogHallbarhet"));
const BlogDigitalisering = lazy(() => import("./pages/BlogDigitalisering"));
const BlogFramtidLaser = lazy(() => import("./pages/BlogFramtidLaser"));
const BlogValjaUnderleverantor = lazy(() => import("./pages/BlogValjaUnderleverantor"));
const BlogOffertforfragan = lazy(() => import("./pages/BlogOffertforfragan"));
const BlogDxfGuide = lazy(() => import("./pages/BlogDxfGuide"));
const BlogOrdlista = lazy(() => import("./pages/BlogOrdlista"));

// Author pages - lazy loaded
const Authors = lazy(() => import("./pages/Authors"));
const AuthorProfile = lazy(() => import("./pages/AuthorProfile"));

// Pillar/hub pages - lazy loaded
const PillarLaser = lazy(() => import("./pages/PillarLaser"));
const PillarKantpress = lazy(() => import("./pages/PillarKantpress"));
const PillarSvets = lazy(() => import("./pages/PillarSvets"));
const PillarMaterial = lazy(() => import("./pages/PillarMaterial"));
const PillarBransch = lazy(() => import("./pages/PillarBransch"));
const PillarGuider = lazy(() => import("./pages/PillarGuider"));
const PillarFramtid = lazy(() => import("./pages/PillarFramtid"));

// Loading fallback for lazy-loaded articles
function ArticleLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
        <span className="text-gray-500 text-sm">Laddar artikel...</span>
      </div>
    </div>
  );
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    const jumpTo = (top: number) => {
      const root = document.documentElement;
      const previousBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = "auto";
      window.scrollTo(0, Math.max(0, top));
      root.style.scrollBehavior = previousBehavior;
    };

    const hash = window.location.hash.slice(1);
    if (!hash) {
      jumpTo(0);
      return;
    }

    let frameId = 0;
    let attempts = 0;
    const scrollToHash = () => {
      const target = document.getElementById(decodeURIComponent(hash));
      if (!target) {
        attempts += 1;
        if (attempts < 120) {
          frameId = window.requestAnimationFrame(scrollToHash);
        }
        return;
      }

      const offset = window.innerWidth >= 1280 ? 96 : 72;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      jumpTo(top);
    };

    frameId = window.requestAnimationFrame(scrollToHash);
    return () => window.cancelAnimationFrame(frameId);
  }, [location]);
  return null;
}
function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<ArticleLoading />}>
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/tjanster" component={Tjanster} />
          <Route path="/tjanster/laserskarning" component={Laserskarning} />
          <Route path="/tjanster/kantpressning" component={Kantpressning} />
          <Route path="/tjanster/svetsning" component={Svetsning} />
          <Route path="/maskinpark" component={Maskinpark} />
          <Route path="/om-oss" component={OmOss} />
          <Route path="/referenser" component={Referenser} />
          <Route path="/referenser/volvo" component={CaseVolvo} />
          <Route path="/referenser/forsvarsindustrin" component={CaseForsvar} />
          <Route path="/kontakt" component={KontaktSSR} />
          <Route path="/policyer" component={Policyer} />
          <Route path="/integritetspolicy" component={Integritetspolicy} />
          <Route path="/villkor" component={Villkor} />
          <Route path="/redaktionell-policy" component={RedaktionellPolicy} />
          <Route path="/laserskarning-stockholm" component={LaserskarningStockholm} />
          <Route path="/laserskarning-vasteras" component={LaserskarningVasteras} />
          <Route path="/laserskarning-orebro" component={LaserskarningOrebro} />
          <Route path="/laserskarning-norrkoping" component={LaserskarningNorrkoping} />
          <Route path="/laserskarning-linkoping" component={LaserskarningLinkoping} />
          <Route path="/laserskarning-eskilstuna" component={LaserskarningEskilstuna} />
          <Route path="/laserskarning-sodertalje" component={LaserskarningSodertalje} />
          <Route path="/laserskarning-strangnas" component={LaserskarningStrangnas} />
          <Route path="/laserskarning-katrineholm" component={LaserskarningKatrineholm} />
          <Route path="/laserskarning-nykoping" component={LaserskarningNykoping} />
          <Route path="/miljopolicy" component={Miljopolicy} />
          <Route path="/arbetsmiljopolicy" component={Arbetsmiljopolicy} />
          <Route path="/kvalitetspolicy" component={Kvalitetspolicy} />
          <Route path="/nyheter" component={Nyheter} />
          <Route path="/nyheter/lissmac-gradningsmaskin" component={BlogLissmac} />
          <Route path="/nyheter/laserskarning-vs-plasmaskaring" component={BlogLaserVsPlasma} />
          <Route path="/nyheter/materialguide-stal-rostfritt-aluminium" component={BlogMaterialguide} />
          <Route path="/nyheter/sa-forbereder-du-cad-filer-for-laserskarning" component={BlogCadFiler} />
          <Route path="/nyheter/laserskarning-tjocklek-guide" component={BlogTjocklekGuide} />
          <Route path="/nyheter/laserskarning-rostfritt-stal" component={BlogRostfritt} />
          <Route path="/nyheter/laserskarning-aluminium" component={BlogAluminium} />
          <Route path="/nyheter/laserskarning-hardox-strenx" component={BlogHardoxStrenx} />
          <Route path="/nyheter/laserskarning-precision-toleranser" component={BlogPrecision} />
          <Route path="/nyheter/laserskarning-kostnader" component={BlogKostnader} />
          <Route path="/nyheter/laserskarning-vs-vattenjet" component={BlogLaserVsVattenjet} />
          <Route path="/nyheter/laserskarning-prototyper" component={BlogPrototyper} />
          <Route path="/nyheter/laserskarning-stora-serier" component={BlogStoraSerier} />
          <Route path="/nyheter/kantpressning-guide" component={BlogKantpressningGuide} />
          <Route path="/nyheter/kantpressning-toleranser" component={BlogKantpressToleranser} />
          <Route path="/nyheter/kantpressning-material" component={BlogKantpressMaterial} />
          <Route path="/nyheter/kantpressning-verktyg" component={BlogKantpressVerktyg} />
          <Route path="/nyheter/kantpressning-konstruktion" component={BlogKantpressKonstruktion} />
          <Route path="/nyheter/kantpressning-vs-rullformning" component={BlogKantpressVsRull} />
          <Route path="/nyheter/svetsning-metoder-industri" component={BlogSvetsMetoder} />
          <Route path="/nyheter/svetsning-rostfritt" component={BlogSvetsRostfritt} />
          <Route path="/nyheter/svetsning-aluminium" component={BlogSvetsAluminium} />
          <Route path="/nyheter/svetsning-kvalitetskontroll" component={BlogSvetsKvalitet} />
          <Route path="/nyheter/svetsning-konstruktion" component={BlogSvetsKonstruktion} />
          <Route path="/nyheter/stalsorter-guide" component={BlogStalsorter} />
          <Route path="/nyheter/rostfritt-304-vs-316" component={BlogRostfritt304vs316} />
          <Route path="/nyheter/aluminium-legeringar" component={BlogAluminiumLegeringar} />
          <Route path="/nyheter/ytbehandling-plat" component={BlogYtbehandling} />
          <Route path="/nyheter/korrosionsskydd-metall" component={BlogKorrosionsskydd} />
          <Route path="/nyheter/materialval-guide" component={BlogMaterialval} />
          <Route path="/nyheter/hardox-slitplat" component={BlogHardoxSlitplat} />
          <Route path="/nyheter/fran-ritning-till-fardig-detalj" component={BlogRitningTillDetalj} />
          <Route path="/nyheter/iso-certifiering-metallbearbetning" component={BlogIsoCertifiering} />
          <Route path="/nyheter/avgradning-ytfinish" component={BlogAvgradning} />
          <Route path="/nyheter/forsta-bit-kontroll" component={BlogForstaBitKontroll} />
          <Route path="/nyheter/nestning-materialoptimering" component={BlogNestning} />
          <Route path="/nyheter/ledtider-leveranstider" component={BlogLedtider} />
          <Route path="/nyheter/laserskarning-fordonsindustrin" component={BlogFordon} />
          <Route path="/nyheter/laserskarning-forsvarsindustrin" component={BlogForsvar} />
          <Route path="/nyheter/laserskarning-byggindustrin" component={BlogBygg} />
          <Route path="/nyheter/laserskarning-jordbruk" component={BlogJordbruk} />
          <Route path="/nyheter/laserskarning-energisektorn" component={BlogEnergi} />
          <Route path="/nyheter/laserskarning-livsmedel" component={BlogLivsmedel} />
          <Route path="/nyheter/laserskarning-marin" component={BlogMarin} />
          <Route path="/nyheter/laserskarning-moblering-design" component={BlogDesign} />
          <Route path="/nyheter/automation-metallbearbetning" component={BlogAutomation} />
          <Route path="/nyheter/hallbar-tillverkning-plat" component={BlogHallbarhet} />
          <Route path="/nyheter/digitalisering-platindustrin" component={BlogDigitalisering} />
          <Route path="/nyheter/framtidens-laserteknik" component={BlogFramtidLaser} />
          <Route path="/nyheter/valja-underleverantor-plat" component={BlogValjaUnderleverantor} />
          <Route path="/nyheter/offertforfragan-guide" component={BlogOffertforfragan} />
          <Route path="/nyheter/dxf-filformat-guide" component={BlogDxfGuide} />
          <Route path="/nyheter/plattillverkning-ordlista" component={BlogOrdlista} />
          <Route path="/kunskapsbank/laserskarning" component={PillarLaser} />
          <Route path="/kunskapsbank/kantpressning" component={PillarKantpress} />
          <Route path="/kunskapsbank/svetsning" component={PillarSvets} />
          <Route path="/kunskapsbank/material" component={PillarMaterial} />
          <Route path="/kunskapsbank/branscher" component={PillarBransch} />
          <Route path="/kunskapsbank/guider" component={PillarGuider} />
          <Route path="/kunskapsbank/framtid" component={PillarFramtid} />
          <Route path="/forfattare/:slug" component={AuthorProfile} />
          <Route path="/forfattare" component={Authors} />
          <Route path="/admin" component={AdminDashboard} />
          <Route path="/tack" component={Tack} />
          <Route path="/404" component={NotFound} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <ClientOnly>
            <Toaster />
          </ClientOnly>
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
